import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { tap, catchError, map, switchMap, finalize, shareReplay } from 'rxjs/operators';
import { Basket, BasketItem, BasketStatus, BasketUploadItemLookup } from '../model/model';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';

/** Response from `POST /CP/basket/calculate-sales-tax/` (LN proxy batch). */
export interface CalculateBatchSalesTaxResponse {
  taxAmountSum?: number | string | null;
  currency?: string | null;
  lineResults?: unknown;
  failedIndex?: number;
  lnBody?: unknown;
  error?: string;
}
import { environment } from 'src/environments/environment';
import { PriceBooksBasketService } from './price-books-basket.service';
import { CpCompanyService } from 'src/app/admin/tf-admin/tf-configuration/tf-company/service/cp-company.service';

@Injectable({
  providedIn: 'root'
})
export class BasketService {
  private apiUrl = environment.apiUrlHost;
  private userId: number;
  private companyTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Tax-related Subjects
  private taxTypeSubject = new BehaviorSubject<string>('No Tax');
  private taxRateSubject = new BehaviorSubject<number>(0);
  /** When set, cart summary tax and total use LN batch `taxAmountSum` instead of the portal tax rate. */
  private lnBatchTaxAmountSubject = new BehaviorSubject<number | null>(null);

  // Observables for tax information
  taxType$ = this.taxTypeSubject.asObservable();
  taxRate$ = this.taxRateSubject.asObservable();
  lnBatchTaxAmount$ = this.lnBatchTaxAmountSubject.asObservable();

  // BehaviorSubject to track item count in active cart
  private itemCountSubject = new BehaviorSubject<number>(0);
  itemCount$ = this.itemCountSubject.asObservable();

  // Store all carts
  private carts: Basket[] = [];
  private basketsListLoaded = false;
  private inFlightBasketsList$: Observable<Basket[]> | null = null;

  // Observable for current cart state
  private cartSubject = new BehaviorSubject<Basket[]>([]);
  cart$ = this.cartSubject.asObservable();

  private regroupBreakPointItemsSubject = new BehaviorSubject<boolean>(false);
  regroupBreakPointItems$ = this.regroupBreakPointItemsSubject.asObservable();

  /** Handoff so shopping-basket opens the basket just created/updated from orders. */
  private static readonly PENDING_ACTIVATE_STORAGE_KEY = 'cpActivateBasket';
  private static readonly PENDING_QUOTATION_UPDATE_STORAGE_KEY = 'cpQuotationUpdateId';

  constructor(
    private http: HttpClient,
    private priceBooksService: PriceBooksBasketService,
    private companyService: CpCompanyService,
  ) {
    this.loadInitialData();
    this.loadCompanyTimezone();
  }

  // Consolidated initial data loading
  loadInitialData(): void {
    this.userId = Number(localStorage.getItem('user'));
    this.getBasketsList();
    this.getTax();
  }

  // Load company timezone
  private loadCompanyTimezone(): void {
    this.companyService.getCompany().subscribe({
      next: (company: any) => {
        if (company?.timezone) {
          this.companyTimezone = company.timezone;
          console.log('Company timezone loaded in BasketService:', this.companyTimezone);
        }
      },
      error: (error) => {
        console.warn('Failed to load company timezone in BasketService, using system default:', error);
      }
    });
  }

  // Get tax information with consistent state management
  getTax(): Observable<void> {
    return this.http.get<any>(`${this.apiUrl}/CP/taxByUserId/`).pipe(
      tap(data => {
        const taxType = data.taxType || 'No Tax';
        const taxRate = data.taxRate || 0;

        this.taxTypeSubject.next(taxType);
        this.taxRateSubject.next(taxRate);
        this.updateAllCartsWithTax();
      }),
      map(() => undefined), // Explicitly return void
      catchError(error => {
        this.taxTypeSubject.next('No Tax');
        this.taxRateSubject.next(0);
        return throwError(error);
      })
    );
  }

  /** Backend may send `minOrderQuantity`, `MIN_ORDER_QUANTITY`, or `min_order_quantity`. */
  private normalizeBasketItemMinOrderQuantity(item: any): number | null {
    const raw = item?.minOrderQuantity ?? item?.MIN_ORDER_QUANTITY ?? item?.min_order_quantity;
    if (raw == null || raw === '') {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  getBasketsList(): Observable<Basket[]> {
    if (this.inFlightBasketsList$) {
      return this.inFlightBasketsList$;
    }

    this.inFlightBasketsList$ = this.http.get<Basket[]>(`${this.apiUrl}/CP/baskets/`).pipe(
      tap(data => {
        console.log('Raw basket data from backend:', data);

        this.carts = data.map(cart => ({
          ...cart,
          delivery_date: cart.delivery_date ? new Date(cart.delivery_date) : null,
          items: cart.items.map((item: any) => ({
            ...item,
            delivery_date: item.delivery_date ? new Date(item.delivery_date) : null,
            originalPrice: item.originalPrice ?? item.price,
            minOrderQuantity: this.normalizeBasketItemMinOrderQuantity(item)
          }))
        }));
        this.basketsListLoaded = true;

        console.log('Processed carts with delivery dates:', this.carts);

        // Update prices for active basket items after loading
        const activeCart = this.carts.find(cart => cart.status === BasketStatus.OPEN);
        if (activeCart && activeCart.items.length > 0) {
          this.updateActiveBasketPrices(activeCart);
        } else {
          this.updateAllCartsWithTax();
          this.cartSubject.next([...this.carts]);
          this.updateItemCount();
        }
      }),
      catchError(error => {
        console.error('Error fetching baskets:', error);
        this.cartSubject.next([...this.carts]);
        return throwError(error);
      }),
      finalize(() => {
        this.inFlightBasketsList$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.inFlightBasketsList$;
  }

  /** Wait for the first successful baskets fetch before treating an empty list as real. */
  ensureBasketsLoaded(): Observable<Basket[]> {
    if (this.basketsListLoaded) {
      return of(this.carts);
    }
    return this.getBasketsList();
  }

  private updateActiveBasketPrices(activeCart: Basket): void {
    let completedUpdates = 0;
    const totalItems = activeCart.items.length;

    if (totalItems === 0) {
      this.updateAllCartsWithTax();
      this.cartSubject.next([...this.carts]);
      this.updateItemCount();
      return;
    }

    activeCart.items.forEach(item => {
      this.priceBooksService.getAccuratePriceForQuantity(item.item, item.quantity).subscribe({
        next: (newPrice) => {
          item.price = newPrice;
          completedUpdates++;

          if (completedUpdates === totalItems) {
            console.log('All active basket prices updated');
            this.updateAllCartsWithTax();
            this.cartSubject.next([...this.carts]);
            this.updateItemCount();
          }
        },
        error: (error) => {
          console.warn(`Failed to update price for item ${item.item}, keeping original price:`, error);
          completedUpdates++;

          if (completedUpdates === totalItems) {
            this.updateAllCartsWithTax();
            this.cartSubject.next([...this.carts]);
            this.updateItemCount();
          }
        }
      });
    });
  }

  // Update all carts with current tax information
  private updateAllCartsWithTax(): void {
    this.carts.forEach(cart => this.calculateTotalWithTax(cart.id));
  }

  // Calculate total with tax (local method, no backend call)
  private calculateTotalWithTax(cartId: number): void {
    const cart = this.getActiveCart();
    if (!cart) return;

    const subtotal = cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const lnTax = this.lnBatchTaxAmountSubject.value;
    const taxAmount =
      lnTax != null && Number.isFinite(lnTax)
        ? lnTax
        : (subtotal * this.taxRateSubject.value) / 100;
    cart.total = subtotal + taxAmount;
  }

  /**
   * Maps each basket line to one LN CalculateSalesTaxAmount payload.
   * Field names must match what your LN proxy expects; adjust here if the backend contract differs.
   */
  mapBasketLinesToLnSalesTaxItems(basket: Basket): Record<string, unknown>[] {
    if (!basket?.items?.length) {
      return [];
    }
    return basket.items.map((line, index) => ({
      itemCode: line.item,
      quantity: line.quantity,
      unitPrice: line.price,
      currency: line.currency,
      unit: line.unit,
      searchKey1: line.searchKey1,
      lineAmount: line.price * line.quantity,
      positionNumber: (index + 1) * 10
    }));
  }

  /**
   * LN batch tax for any basket (e.g. confirmation page) without mutating cart state.
   */
  calculateSalesTaxForBasket(basket: Basket): Observable<number | null> {
    if (!basket?.items?.length) {
      return of(null);
    }

    const items = this.mapBasketLinesToLnSalesTaxItems(basket);
    return this.http
      .post<CalculateBatchSalesTaxResponse>(`${this.apiUrl}/CP/basket/calculate-sales-tax/`, { items })
      .pipe(
        map((res) => {
          const raw = res?.taxAmountSum;
          const n =
            typeof raw === 'number'
              ? raw
              : raw != null && raw !== ''
                ? Number(raw)
                : NaN;
          return Number.isFinite(n) ? n : null;
        }),
        catchError((err) => {
          console.warn('Batch sales tax (LN proxy) request failed:', err);
          return of(null);
        }),
      );
  }

  /**
   * POST batch sales tax for the active open basket; updates {@link lnBatchTaxAmount$} and cart totals.
   * On failure, falls back to portal tax rate for totals.
   */
  refreshLnSalesTax(): Observable<CalculateBatchSalesTaxResponse | null> {
    const cart = this.getActiveCart();
    if (!cart?.items?.length) {
      this.lnBatchTaxAmountSubject.next(null);
      this.updateAllCartsWithTax();
      this.cartSubject.next([...this.carts]);
      return of(null);
    }

    const items = this.mapBasketLinesToLnSalesTaxItems(cart);
    return this.http
      .post<CalculateBatchSalesTaxResponse>(`${this.apiUrl}/CP/basket/calculate-sales-tax/`, { items })
      .pipe(
        tap(res => {
          const raw = res?.taxAmountSum;
          const n =
            typeof raw === 'number'
              ? raw
              : raw != null && raw !== ''
                ? Number(raw)
                : NaN;
          this.lnBatchTaxAmountSubject.next(Number.isFinite(n) ? n : null);
          this.updateAllCartsWithTax();
          this.cartSubject.next([...this.carts]);
        }),
        catchError(err => {
          console.warn('Batch sales tax (LN proxy) request failed:', err);
          this.lnBatchTaxAmountSubject.next(null);
          this.updateAllCartsWithTax();
          this.cartSubject.next([...this.carts]);
          return of(null);
        })
      );
  }

  // Create a basket with consistent state management
  createBasket(name: string, initialItem?: any | null): Observable<Basket> {
    const taxRate = this.taxRateSubject.value;
    const totalWithTax = initialItem
      ? initialItem.price * initialItem.quantity * (1 + taxRate / 100)
      : 0;

    const newBasket: Basket = {
      id: 0,
      status: BasketStatus.OPEN,
      user: this.userId,
      name,
      items: initialItem ? [initialItem] : [],
      total: totalWithTax,
      createdAt: null,
      updatedAt: null
    };

    return this.http.post<Basket>(`${this.apiUrl}/CP/basket/`, newBasket).pipe(
      tap(response => {
        // Only add to local state if backend call succeeds
        this.carts.push(response);
        this.cartSubject.next([...this.carts]);
        this.updateItemCount(); // Update item count after basket creation
      }),
      switchMap(response => {
        // If initial item exists, preload its price book
        if (initialItem && initialItem.item) {
          return this.preloadPriceBooksForBasket({ items: [{ item: initialItem.item }] }).pipe(
            map(() => response), // Return the basket response
            catchError(priceError => {
              console.warn('Failed to load price book for initial item in new basket:', priceError);
              // Return the basket even if price book loading fails
              return of(response);
            })
          );
        }
        return of(response);
      }),
      catchError(error => {
        // Revert any potential local changes
        return throwError(error);
      })
    );
  }

  private findBasketLine(
    cart: Basket | undefined,
    itemId: string,
    basketLineId?: string | number | null
  ): BasketItem | undefined {
    if (!cart) {
      return undefined;
    }
    if (basketLineId != null && basketLineId !== '') {
      const byId = cart.items.find(
        i => i.item == itemId && i.id != null && String(i.id) === String(basketLineId)
      );
      if (byId) {
        return byId;
      }
    }
    return cart.items.find(i => i.item == itemId);
  }

  // Legacy updateQuantity lookup: cart?.items.find(i => i.item == itemId) — one line per product code

  updateQuantity(
    cartId: number,
    itemId: string,
    quantity: number,
    basketLineId?: string | number | null
  ): Observable<number> {
    const cart = this.getCartById(cartId);
    const originalItem = this.findBasketLine(cart, itemId, basketLineId);

    if (!cart || !originalItem) {
      return throwError(() => new Error('Cart or item not found'));
    }

    // Store original state for rollback
    const originalQuantity = originalItem.quantity;
    const originalPrice = originalItem.price;

    // Get accurate price using the price API
    return this.priceBooksService.getAccuratePriceForQuantity(itemId, quantity).pipe(
      switchMap(newPrice => {
        // Update item with new quantity and the accurately calculated price
        originalItem.quantity = quantity;
        originalItem.price = newPrice;
        this.calculateTotalWithTax(cartId);
        this.cartSubject.next([...this.carts]);

        // Save to backend
        return this.saveBasket(cart).pipe(
          map(() => newPrice),
          catchError(saveError => {
            console.error("Failed to save basket after quantity update:", saveError);
            // Revert changes if save fails
            originalItem.quantity = originalQuantity;
            originalItem.price = originalPrice;
            this.calculateTotalWithTax(cartId);
            this.cartSubject.next([...this.carts]);
            return throwError(() => saveError);
          })
        );
      }),
      catchError(priceError => {
        console.error("Failed to get price for quantity update:", priceError);
        // Ensure original state is maintained on price fetch failure
        originalItem.quantity = originalQuantity;
        originalItem.price = originalPrice;
        this.calculateTotalWithTax(cartId);
        this.cartSubject.next([...this.carts]);
        return throwError(() => priceError);
      })
    );
  }

  preloadPriceBooksForBasket(cart: any): Observable<any> {
    if (cart?.items?.length) {
      const missing = cart.items
        .map((item: any) => item.item)
        .filter(code => !this.priceBooksService.hasPriceBook(code));

      if (missing.length) return this.priceBooksService.fetchPriceBook(missing);
      console.log('All price books already cached');
    } else {
      console.log('No items in cart for preloading');
    }

    return of([]);
  }

  // Update basket with consistent state management
  updateBasket(cart: Basket): Observable<void> {
    // Store original cart state for potential rollback
    const originalCart = this.getActiveCart();

    return this.http.put<void>(`${this.apiUrl}/update`, cart).pipe(
      tap(() => {
        const index = this.carts.findIndex(c => c.id === cart.id);
        if (index !== -1) {
          this.carts[index] = cart;
          this.calculateTotalWithTax(cart.id);
          this.cartSubject.next([...this.carts]);
          this.updateItemCount(); // Update item count after basket creation
        }
      }),
      catchError(error => {
        // Rollback to original cart state if backend call fails
        if (originalCart) {
          const index = this.carts.findIndex(c => c.id === cart.id);
          if (index !== -1) {
            this.carts[index] = originalCart;
            this.calculateTotalWithTax(originalCart.id);
            this.cartSubject.next([...this.carts]);
            this.updateItemCount(); // Update item count after basket creation
          }
        }
        return throwError(error);
      })
    );
  }

  updateItemCount(): void {
    console.log("updating the item count")
    const activeCart = this.getActiveCart();
    const itemCount = activeCart ? activeCart.items.length : 0;
    this.itemCountSubject.next(itemCount); // Update the item count observable
    console.log("itemCount",itemCount)
  }

  // Swap items between carts with consistent state management
  swapItemToAnotherCart(sourceCartId: number, targetCartId: number, itemId: string): Observable<void> {
    console.log(`Swapping item ${itemId} from cart ${sourceCartId} to cart ${targetCartId}`);
    const sourceCart = this.getCartById(sourceCartId);
    const targetCart = this.getCartById(targetCartId);
    console.log(sourceCart, targetCart);

    if (!sourceCart || !targetCart) {
      return throwError(new Error('Invalid source or target cart'));
    }

    const itemIndex = sourceCart.items.findIndex(item => item.item === itemId);
    if (itemIndex === -1) {
      return throwError(new Error('Item not found in source cart'));
    }

    const itemToSwap = sourceCart.items[itemIndex];
    
    // Check if target cart already has the same item
    const existingItemIndex = targetCart.items.findIndex(item => item.item === itemId);
    const hasExistingItem = existingItemIndex !== -1;

    // If target cart has the same item, we need to merge quantities
    if (hasExistingItem) {
      const existingItem = targetCart.items[existingItemIndex];
      const combinedQuantity = existingItem.quantity + itemToSwap.quantity;
      
      // Get accurate price for the combined quantity
      return this.priceBooksService.getAccuratePriceForQuantity(itemId, combinedQuantity).pipe(
        switchMap(updatedPrice => {
          // Store original state for potential rollback
          const originalSourceCartItems = [...sourceCart.items];
          const originalTargetCartItems = [...targetCart.items];

          // Prepare the swap payload
          const swapPayload = {
            sourceCartId,
            targetCartId,
            itemId
          };

          return this.http.put<void>(`${this.apiUrl}/CP/basket/swap/`, swapPayload).pipe(
            tap(() => {
              // Update existing item in target cart with merged quantity and price
              existingItem.quantity = combinedQuantity;
              existingItem.price = updatedPrice;
              
              // Remove item from source cart
              sourceCart.items.splice(itemIndex, 1);

              // Recalculate totals
              this.calculateTotalWithTax(sourceCartId);
              this.calculateTotalWithTax(targetCartId);
              this.cartSubject.next([...this.carts]);
              this.updateItemCount();
            }),
            switchMap(() =>
              this.tryDeleteEmptyBasket(sourceCartId, sourceCart),
            ),
            catchError(error => {
              // Rollback to original state if backend call fails
              sourceCart.items = originalSourceCartItems;
              targetCart.items = originalTargetCartItems;
              this.calculateTotalWithTax(sourceCartId);
              this.calculateTotalWithTax(targetCartId);
              this.cartSubject.next([...this.carts]);
              return throwError(error);
            })
          );
        }),
        catchError(priceError => {
          console.warn(`Failed to get accurate price for merged quantity, proceeding with current price:`, priceError);
          // Proceed with merge using current prices if price fetch fails
          const originalSourceCartItems = [...sourceCart.items];
          const originalTargetCartItems = [...targetCart.items];

          // Get existing item reference again to ensure we have the current one
          const existingItemInTarget = targetCart.items[existingItemIndex];
          const combinedQuantityForError = existingItemInTarget.quantity + itemToSwap.quantity;

          const swapPayload = {
            sourceCartId,
            targetCartId,
            itemId
          };

          return this.http.put<void>(`${this.apiUrl}/CP/basket/swap/`, swapPayload).pipe(
            tap(() => {
              // Merge quantities without price update (using existing price)
              existingItemInTarget.quantity = combinedQuantityForError;
              
              // Remove item from source cart
              sourceCart.items.splice(itemIndex, 1);

              this.calculateTotalWithTax(sourceCartId);
              this.calculateTotalWithTax(targetCartId);
              this.cartSubject.next([...this.carts]);
              this.updateItemCount();
            }),
            switchMap(() => this.tryDeleteEmptyBasket(sourceCartId, sourceCart)),
            catchError(error => {
              sourceCart.items = originalSourceCartItems;
              targetCart.items = originalTargetCartItems;
              this.calculateTotalWithTax(sourceCartId);
              this.calculateTotalWithTax(targetCartId);
              this.cartSubject.next([...this.carts]);
              return throwError(error);
            })
          );
        })
      );
    }

    // Get accurate price for the item's current quantity before swapping (when item doesn't exist in target)
    return this.priceBooksService.getAccuratePriceForQuantity(itemId, itemToSwap.quantity).pipe(
      switchMap(updatedPrice => {
        // Store original state for potential rollback
        const originalSourceCartItems = [...sourceCart.items];
        const originalTargetCartItems = [...targetCart.items];

        // Update the price with accurate value
        itemToSwap.price = updatedPrice;

        // Prepare the swap payload
        const swapPayload = {
          sourceCartId,
          targetCartId,
          itemId
        };

        return this.http.put<void>(`${this.apiUrl}/CP/basket/swap/`, swapPayload).pipe(
          tap(() => {
            // Move item only after successful backend call
            const [item] = sourceCart.items.splice(itemIndex, 1);
            targetCart.items.push(item);

            // Recalculate totals
            this.calculateTotalWithTax(sourceCartId);
            this.calculateTotalWithTax(targetCartId);
            this.cartSubject.next([...this.carts]);
            this.updateItemCount();
          }),
          switchMap(() => this.tryDeleteEmptyBasket(sourceCartId, sourceCart)),
          catchError(error => {
            // Rollback to original state if backend call fails
            sourceCart.items = originalSourceCartItems;
            targetCart.items = originalTargetCartItems;
            this.calculateTotalWithTax(sourceCartId);
            this.calculateTotalWithTax(targetCartId);
            this.cartSubject.next([...this.carts]);
            return throwError(error);
          })
        );
      }),
      catchError(priceError => {
        console.warn(`Failed to get accurate price for swap, proceeding with current price:`, priceError);
        // Proceed with swap using current price if price fetch fails
        const originalSourceCartItems = [...sourceCart.items];
        const originalTargetCartItems = [...targetCart.items];

        const swapPayload = {
          sourceCartId,
          targetCartId,
          itemId
        };

        return this.http.put<void>(`${this.apiUrl}/CP/basket/swap/`, swapPayload).pipe(
          tap(() => {
            const [item] = sourceCart.items.splice(itemIndex, 1);
            targetCart.items.push(item);
            this.calculateTotalWithTax(sourceCartId);
            this.calculateTotalWithTax(targetCartId);
            this.cartSubject.next([...this.carts]);
            this.updateItemCount();
          }),
          switchMap(() => this.tryDeleteEmptyBasket(sourceCartId, sourceCart)),
          catchError(error => {
            sourceCart.items = originalSourceCartItems;
            targetCart.items = originalTargetCartItems;
            this.calculateTotalWithTax(sourceCartId);
            this.calculateTotalWithTax(targetCartId);
            this.cartSubject.next([...this.carts]);
            return throwError(error);
          })
        );
      })
    );
  }

  // Delete item from cart with consistent state management
deleteItemFromCart(cartId: number, itemId: string): Observable<void> {
  const cart = this.getCartById(cartId);
  if (!cart) {
    return throwError(new Error('Cart not found'));
  }

  // Store original state for rollback in case of failure
  const originalItems = [...cart.items];

  const encodedItemId = encodeURIComponent(itemId);
  return this.http.delete<void>(`${this.apiUrl}/CP/basket/${cartId}/item/${encodedItemId}/delete/`).pipe(
    tap(() => {
      // Remove item only after successful backend deletion
      cart.items = cart.items.filter(item => item.item !== itemId);

      this.calculateTotalWithTax(cartId);
      this.cartSubject.next([...this.carts]);
      console.log("deleting item")
      this.updateItemCount(); // Update item count after deletion
    }),
    switchMap(() => this.tryDeleteEmptyBasket(cartId, cart)),
    catchError(error => {
      // Rollback changes if API call fails
      cart.items = originalItems;
      this.cartSubject.next([...this.carts]);
      return throwError(error);
    })
  );
}

  // Save basket with consistent state management
  saveBasket(cart: Basket): Observable<Basket> {
    console.log('Saving basket with delivery dates initial:', cart);
    
    // Format dates for backend
    const formattedDeliveryDate = cart.delivery_date ? this.formatDateForBackend(cart.delivery_date) : null;
    console.log('Formatted basket delivery_date:', formattedDeliveryDate);
    
    const basketToSave = {
      ...cart,
      delivery_date: formattedDeliveryDate,
      items: cart.items.map(item => {
        const formattedItemDate = item.delivery_date ? this.formatDateForBackend(item.delivery_date) : null;
        if (item.delivery_date) {
          console.log(`Formatted item ${item.item} delivery_date:`, formattedItemDate);
        }
        return {
          ...item,
          delivery_date: formattedItemDate,
          item: item.item,
        };
      })
    };

    console.log('Saving basket with delivery dates:', JSON.stringify(basketToSave, null, 2));
    const originalCart = this.getActiveCart();

    return this.http.put<Basket>(`${this.apiUrl}/CP/basket/${cart.id}/update/`, basketToSave).pipe(
      tap((response) => {
        console.log('Backend response:', response);

        const index = this.carts.findIndex(c => c.id === cart.id);
        if (index !== -1) {
          // Preserve the local item order while updating with backend data
          const localCart = this.carts[index];

          // Create a map of backend items for quick lookup (supports duplicate product codes)
          const resolveBackendItem = (localItem: BasketItem, localIndex: number): BasketItem | undefined => {
            if (localItem.id != null && localItem.id !== '') {
              const byId = response.items.find(
                item =>
                  item.item === localItem.item && String(item.id) === String(localItem.id)
              );
              if (byId) {
                return byId;
              }
            }
            const sameItemLocalIndex = localCart.items
              .slice(0, localIndex + 1)
              .filter(item => item.item === localItem.item).length - 1;
            return response.items.filter(item => item.item === localItem.item)[sameItemLocalIndex];
          };

          // Legacy saveBasket merge (one line per product code):
          // const backendItemsMap = new Map(response.items.map(item => [item.item, item]));
          // const backendItem = backendItemsMap.get(localItem.item);

          // Update local items while preserving their order AND originalPrice
          const updatedItems = localCart.items.map((localItem, localIndex) => {
            const backendItem = resolveBackendItem(localItem, localIndex);
            if (backendItem) {
              return {
                ...backendItem,
                delivery_date: backendItem.delivery_date ? new Date(backendItem.delivery_date) : null,
                // PRESERVE the originalPrice from local item, don't let backend overwrite it
                originalPrice: localItem.originalPrice ?? localItem.price,
                minOrderQuantity: this.normalizeBasketItemMinOrderQuantity(backendItem)
              };
            }
            return {
              ...localItem,
              delivery_date: localItem.delivery_date ? new Date(localItem.delivery_date) : null,
              // Ensure originalPrice is set if not already present
              originalPrice: localItem.originalPrice ?? localItem.price
            };
          });

          const updatedCart = {
            ...response,
            delivery_date: response.delivery_date ? new Date(response.delivery_date) : null,
            items: updatedItems // Use the order-preserved items with originalPrice intact
          };

          this.carts[index] = updatedCart;
          this.calculateTotalWithTax(cart.id);
          this.cartSubject.next([...this.carts]);
        }
        this.updateItemCount();
      }),
      catchError(error => {
        console.error('Failed to save basket with delivery dates:', error);
        console.error('Error details:', error.error);
        console.error('Request payload that failed:', JSON.stringify(basketToSave, null, 2));
        if (originalCart) {
          const index = this.carts.findIndex(c => c.id === cart.id);
          if (index !== -1) {
            this.carts[index] = originalCart;
            this.calculateTotalWithTax(originalCart.id);
            this.cartSubject.next([...this.carts]);
          }
        }
        return throwError(error);
      })
    );
  }

  formatDeliveryDateForBackend(date: Date | string | null | undefined): string | null {
    return this.formatDateForBackend(date);
  }

private formatDateForBackend(date: any): string | null {
  if (!date) return null;

  // If it's already a date-only string, return as-is (for backward compatibility)
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  // If it's already an ISO datetime string with timezone, return as-is
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}$/.test(date)) {
    return date;
  }

  const d = new Date(date);
  if (isNaN(d.getTime())) {
    console.warn('Invalid date provided to formatDateForBackend:', date);
    return null;
  }
  
  // Validate company timezone is loaded
  if (!this.companyTimezone || this.companyTimezone === '') {
    console.warn('Company timezone not loaded, using system default');
    // Fallback: use ISO string with local timezone offset
    const isoString = d.toISOString();
    return isoString.slice(0, 19) + isoString.slice(23); // Remove 'Z' and add offset
  }

  // Format the date as it appears in the company timezone
  // This ensures: Wed Dec 17 2025 14:01:00 GMT+0100 becomes 2025-12-17T14:01:00+01:00
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: this.companyTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const parts = formatter.formatToParts(d);
  const year = parts.find(p => p.type === 'year')?.value || '';
  const month = parts.find(p => p.type === 'month')?.value || '';
  const day = parts.find(p => p.type === 'day')?.value || '';
  const hour = parts.find(p => p.type === 'hour')?.value || '';
  const minute = parts.find(p => p.type === 'minute')?.value || '';
  const second = parts.find(p => p.type === 'second')?.value || '00';

  // Calculate timezone offset for company timezone at this specific date
  // Strategy: Create a UTC date with the same components, format it in company TZ,
  // and calculate the difference to get the offset
  const compYear = parseInt(year);
  const compMonth = parseInt(month) - 1;
  const compDay = parseInt(day);
  const compHour = parseInt(hour);
  const compMinute = parseInt(minute);
  const compSecond = parseInt(second);

  // Create a UTC date with these components (this represents what UTC time would be
  // if we treated the company timezone components as UTC)
  const utcGuess = new Date(Date.UTC(compYear, compMonth, compDay, compHour, compMinute, compSecond));
  
  // Format this UTC guess in company timezone to see what time it shows
  const companyParts = formatter.formatToParts(utcGuess);
  const companyHour = parseInt(companyParts.find(p => p.type === 'hour')?.value || '0');
  const companyMinute = parseInt(companyParts.find(p => p.type === 'minute')?.value || '0');
  
  // Calculate offset: how many hours/minutes difference
  const hourDiff = compHour - companyHour;
  const minDiff = compMinute - companyMinute;
  const totalOffsetMinutes = hourDiff * 60 + minDiff;
  
  // Adjust the UTC guess by the offset to get the correct UTC time
  const correctUtc = new Date(utcGuess.getTime() - (totalOffsetMinutes * 60 * 1000));
  
  // Now format the correct UTC time in company timezone to verify and get final offset
  const finalParts = formatter.formatToParts(correctUtc);
  const finalCompanyHour = parseInt(finalParts.find(p => p.type === 'hour')?.value || '0');
  const finalCompanyMin = parseInt(finalParts.find(p => p.type === 'minute')?.value || '0');
  
  // If we're still off, adjust once more (should be very close now)
  const finalHourDiff = compHour - finalCompanyHour;
  const finalMinDiff = compMinute - finalCompanyMin;
  const finalTotalOffsetMinutes = finalHourDiff * 60 + finalMinDiff;
  const finalCorrectUtc = new Date(correctUtc.getTime() - (finalTotalOffsetMinutes * 60 * 1000));
  
  // Now we have the correct UTC time. Calculate the offset from UTC
  // The offset is: (correctUtc in company TZ) - (correctUtc in UTC)
  // Format correctUtc in UTC to compare
  const utcFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  const utcPartsFinal = utcFormatter.formatToParts(finalCorrectUtc);
  const utcHour = parseInt(utcPartsFinal.find(p => p.type === 'hour')?.value || '0');
  const utcMinute = parseInt(utcPartsFinal.find(p => p.type === 'minute')?.value || '0');
  
  // Offset = company time - UTC time
  const offsetHours = compHour - utcHour;
  const offsetMinutes = compMinute - utcMinute;
  const totalOffsetMins = offsetHours * 60 + offsetMinutes;
  
  // Format offset
  const offsetHrs = Math.floor(Math.abs(totalOffsetMins) / 60);
  const offsetMins = Math.abs(totalOffsetMins) % 60;
  const offsetSign = totalOffsetMins >= 0 ? '+' : '-';

  const formattedDate = `${year}-${month}-${day}T${hour}:${minute}:${second}${offsetSign}${String(offsetHrs).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;
  
  // Validate the formatted date
  const dateValidationRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}$/;
  if (!dateValidationRegex.test(formattedDate)) {
    console.error('Invalid date format generated:', formattedDate);
    console.error('Input date:', date, 'Company timezone:', this.companyTimezone);
    // Fallback to ISO format
    const isoString = d.toISOString();
    return isoString.slice(0, 19) + isoString.slice(23); // Remove 'Z' and add offset
  }
  
  return formattedDate;
}

  // Delete basket with consistent state management
  deleteBasket(cartId: number): Observable<void> {
    const originalCart = this.getCartById(cartId);
    const isActiveCart = originalCart?.status === BasketStatus.OPEN;

    console.log("Attempting to delete basket with ID:", cartId);
    console.log("Is active cart:", isActiveCart);

    return this.http.delete<void>(`${this.apiUrl}/CP/basket/${cartId}/delete/`).pipe(
      tap(() => {
        console.log("Delete API call successful for basket ID:", cartId);
        this.carts = this.carts.filter(cart => cart.id !== cartId);
        console.log("Carts after local filter:", this.carts.map(c => ({ id: c.id, name: c.name })));

        // Emit the updated carts list to trigger component refresh
        this.cartSubject.next([...this.carts]);
        this.updateItemCount();

        // If we deleted the active cart, emit again to ensure component state updates
        if (isActiveCart) {
          // Small delay to ensure component processes the change
          setTimeout(() => {
            this.cartSubject.next([...this.carts]);
          }, 0);
        }
      }),
      catchError(error => {
        console.error("Delete API call failed for basket ID:", cartId, error);
        if (originalCart) {
          this.carts.push(originalCart);
          this.cartSubject.next([...this.carts]);
          this.updateItemCount();
        }
        return throwError(error);
      })
    );
  }

  private tryDeleteEmptyBasket(cartId: number, cart: Basket): Observable<void> {
    if (cart.items.length === 0) {
      return this.deleteBasket(cartId);
    }

    return of(void 0);
  }

  // Send support request with consistent state management
  sendSupportRequest(question: string, description: string, selectedItems: BasketItem[]): Observable<any> {
    const payload = {
      question,
      description,
      selectedItems
    };
    return this.http.post(`${this.apiUrl}/CP/support-request/`, payload);
  }

  getActiveCart(): Basket | undefined {
    return this.carts.find(cart => cart.status === BasketStatus.OPEN);
  }

  getCartById(cartId: number): Basket | undefined {
    console.log(this.carts)
    return this.carts.find(cart => cart.id === cartId);
  }

  /**
   * Get all available baskets (excluding confirmed ones)
   * @returns Array of available baskets
   */
  getAvailableBaskets(): Basket[] {
    return this.carts.filter(basket => basket.status !== BasketStatus.CONFIRMED);
  }

  public saveorder(combinedData): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/CP/SalesOrder/`, combinedData, {
        headers: { 'Content-Type': 'application/json' }
    });
  }

  public sendSalesOrder(SalesOrderData: any): Observable<any | HttpErrorResponse> {
    return this.http.put<any>(`${this.apiUrl}/CP/SalesOrderLNAPI`, SalesOrderData)
  }

  getConfirmationInfo(): Observable<any> {
    return this.http.get(`${this.apiUrl}/CP/SalesOrder/`);
  }

  /**
   * Ship-to / BP address references (customer portal).
   * The backend rejects the call when `businessPartner` is missing, so the code is required here.
   * Response is paginated: `{ pagination, results }` (query: `page`, `page_size`).
   */
  getBPartnerAddressRefs(
    businessPartnerCode: string,
    params?: { page?: number; page_size?: number }
  ): Observable<any> {
    const code = businessPartnerCode != null ? String(businessPartnerCode).trim() : '';
    if (code.length === 0) {
      return throwError(() => new Error('cp_BPartnerAddressRefs requires a business partner code'));
    }
    const page = params?.page != null && params.page > 0 ? params.page : 1;
    const pageSize =
      params?.page_size != null && params.page_size > 0 ? Math.min(params.page_size, 100) : 10;
    const httpParams = new HttpParams().set('page', String(page)).set('page_size', String(pageSize));
    return this.http.post<any>(
      `${this.apiUrl}/CP/cp_BPartnerAddressRefs/`,
      { businessPartner: code },
      {
        headers: { 'Content-Type': 'application/json' },
        params: httpParams
      }
    );
  }

  public getProductById(productId: string): Observable<any> {
    const query1 = new HttpParams()
      .set('itemCode', productId);
    return this.http.get(`${this.apiUrl}/CF/item-details/`, { params: query1 });
  }

  /** Basket upload: resolves published and unpublished items with quantity-aware pricing. */
  lookupUploadItem(itemCode: string, quantity: number): Observable<BasketUploadItemLookup> {
    const params = new HttpParams()
      .set('itemCode', itemCode)
      .set('quantity', String(quantity));
    return this.http.get<BasketUploadItemLookup>(`${this.apiUrl}/CP/basket/item-lookup/`, { params });
  }

  getBasketById(cartId: number): Observable<Basket> {
    return this.http.get<Basket>(`${this.apiUrl}/CP/basket/${cartId}/`).pipe(
      catchError(error => throwError(error))
    );
  }

  /** Makes the linked quotation basket the active open cart for add-to-basket flows. */
  activateBasketById(basketId: number): Observable<Basket> {
    const targetBasket = this.getCartById(basketId);
    if (!targetBasket) {
      return throwError(() => new Error('Basket not found'));
    }

    const activeCart = this.getActiveCart();
    if (activeCart?.id === basketId) {
      return of(targetBasket);
    }

    const demoteActive$ = activeCart
      ? (() => {
          activeCart.status = BasketStatus.DRAFT;
          return this.saveBasket(activeCart);
        })()
      : of(null);

    return demoteActive$.pipe(
      switchMap(() => {
        targetBasket.status = BasketStatus.OPEN;
        return this.saveBasket(targetBasket).pipe(
          tap(() => {
            this.cartSubject.next([...this.carts]);
            this.updateItemCount();
          }),
        );
      }),
    );
  }

  generateUniqueBasketName(): Observable<{ name: string }> {
    return this.http.get<{ name: string }>(`${this.apiUrl}/CP/basket/generate-name/`);
  }

  setPendingActivateBasket(id: string | number | null, name: string | null): void {
    const payload = {
      id: id != null && String(id).trim() !== '' ? String(id).trim() : null,
      name: name && name.trim() ? name.trim() : null,
    };
    if (!payload.id && !payload.name) {
      sessionStorage.removeItem(BasketService.PENDING_ACTIVATE_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(BasketService.PENDING_ACTIVATE_STORAGE_KEY, JSON.stringify(payload));
  }

  peekPendingActivateBasket(): { id: string | null; name: string | null } | null {
    try {
      const raw = sessionStorage.getItem(BasketService.PENDING_ACTIVATE_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      const id = parsed?.id != null && String(parsed.id).trim() !== '' ? String(parsed.id).trim() : null;
      const name = parsed?.name && String(parsed.name).trim() ? String(parsed.name).trim() : null;
      if (!id && !name) {
        return null;
      }
      return { id, name };
    } catch {
      return null;
    }
  }

  clearPendingActivateBasket(): void {
    sessionStorage.removeItem(BasketService.PENDING_ACTIVATE_STORAGE_KEY);
  }

  setPendingQuotationUpdate(quotationId: string | null): void {
    const id = quotationId?.trim() ?? '';
    if (!id) {
      sessionStorage.removeItem(BasketService.PENDING_QUOTATION_UPDATE_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(BasketService.PENDING_QUOTATION_UPDATE_STORAGE_KEY, id);
  }

  peekPendingQuotationUpdate(): string | null {
    try {
      const raw = sessionStorage.getItem(BasketService.PENDING_QUOTATION_UPDATE_STORAGE_KEY);
      const id = raw?.trim() ?? '';
      return id || null;
    } catch {
      return null;
    }
  }

  clearPendingQuotationUpdate(): void {
    sessionStorage.removeItem(BasketService.PENDING_QUOTATION_UPDATE_STORAGE_KEY);
  }

  findBasketForActivate(
    baskets: Basket[],
    pending: { id: string | null; name: string | null } | null,
    queryKey?: string | null
  ): Basket | undefined {
    if (!baskets?.length) {
      return undefined;
    }
    const id = pending?.id || null;
    const name = pending?.name || null;
    const key = queryKey?.trim() || '';
    return (
      (id ? baskets.find((b) => String(b.id) === id) : undefined) ||
      (name ? baskets.find((b) => (b.name || '').trim() === name) : undefined) ||
      (key ? baskets.find((b) => String(b.id) === key) : undefined) ||
      (key ? baskets.find((b) => (b.name || '').trim() === key) : undefined)
    );
  }

  /** Demotes the current open basket to draft, then creates a new open basket. */
  createNewBasketFromActive(activeBasket?: Basket): Observable<Basket> {
    const demoteActive$ = activeBasket
      ? (() => {
          activeBasket.status = BasketStatus.DRAFT;
          return this.saveBasket(activeBasket);
        })()
      : of(null);

    return demoteActive$.pipe(
      switchMap(() =>
        this.generateUniqueBasketName().pipe(switchMap(({ name }) => this.createBasket(name))),
      ),
    );
  }

  updateBasketName(name: string, oldName: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/CP/basket/rename/${name}/`, {oldName: oldName});
  }

  getRegroupBreakPointItems(): boolean {
    return this.regroupBreakPointItemsSubject.value;
  }

  setRegroupBreakPointItems(regroup: boolean): void {
    if (this.regroupBreakPointItemsSubject.value === regroup) {
      return;
    }
    this.regroupBreakPointItemsSubject.next(regroup);
  }
}
